import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import HorizontalStack from 'Components/ComponentLibrary/Atoms/Layout/HorizontalStack';
import StackItem from 'Components/ComponentLibrary/Atoms/Layout/StackItem';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { noop } from 'Helpers/PropTypes';
import { getBorder } from 'Components/utils';
import RichTextDisplayEditor from 'Components/ComponentLibrary/Components/Uncategorized/RichTextDisplayEditor';
import RichTextEditor from 'Components/UI/RichTextEditor';
import { useSnackbarContext } from 'Context/SnackbarContext';
import DeleteButtonWithConfirmation from 'Components/ComponentLibrary/Components/Buttons/DeleteButtonWithConfirmation';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import MetaField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import { FDBodyText } from '@facet-wealth/design';
import AutosaveStatusMessage from './AutosaveStatusMessage';

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: `${theme.spacing(2)}px`,
    padding: theme.spacing(2, 3, 3, 3),
    ...getBorder(theme, { bottom: true, color: theme.palette.facet.lightGray }),
  },
  helperOverride: {
    display: 'none',
  },
  message: {
    paddingBottom: 0,
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  deletebutton: {
    margin: 0,
  },
});

const InsightSection = ({
  className,
  classes,
  onSubmit,
}) => {
  const {
    errors,
    resource,
    saving,
  } = useMetaLayoutContext();
  const { logStandardizedEvent } = useEventTrackingLogEvent();

  const [editing, setEditing] = useState(false);

  const originalApplyToAllValue = useMemo(() => {
    return resource.attr('applyToAll') === 'true';
  }, [resource]);


  const { refreshPageContentCollection } = usePageContentContext();

  const { displayErrorMessage } = useSnackbarContext();

  const canDelete = resource.meta().canDelete();

  const handleDelete = React.useCallback(async () => {
    try {
      await resource.delete();
      refreshPageContentCollection();
      logStandardizedEvent(categories.whatIf, eventTypes.deleteSmartInsight, 'Scenario Insight - Insight deleted');
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  }, [resource]);


  const handleSummarySubmit = React.useCallback(async (value) => {
    try {
      resource.attr('content', value);
      await resource.submit();
      onSubmit();
      logStandardizedEvent(categories.whatIf, eventTypes.updateSmartInsight, 'Scenario Insight - Insight updated');
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  }, [resource]);

  return (
    <Box className={classNames(classes.root, className)}>
      <Box>
        {editing && originalApplyToAllValue && <MetaField attr={'applyToAll'} />}
        <RichTextDisplayEditor
          content={resource.attr('content')}
          onSubmit={handleSummarySubmit}
          placeholderLabel={'Click to edit insights'}
          RichTextEditorProps={{
            test: 'facet-plan-cfp-insights-ck-editor',
          }}
          EditorComponent={RichTextEditor}
          onEditEnter={() => setEditing(true)}
          onEditExit={() => setEditing(false)}
        />
        {originalApplyToAllValue && <FDBodyText size={'sm'} value={'Common across all scenarios'} />}
        <HorizontalStack spacing={2}>
          <StackItem fillsSpace>
            <Box className={classes.footer}>
              <AutosaveStatusMessage
                errors={errors}
                isDirty={resource.isDirty()}
                isSaving={saving}
                lastSaved={resource.attr('lastSaved')}
              />
              {canDelete
                ? (
                  <DeleteButtonWithConfirmation
                    onDelete={handleDelete}
                    dialogProps={{
                      title: 'Are you sure?',
                      message: 'Would you like to permanently delete this item? This action cannot be undone.',
                      cancelLabel: 'Cancel',
                      confirmLabel: 'Delete',
                      closeOnSubmit: true,
                    }}
                  />
                ) : null
            }
            </Box>
          </StackItem>
        </HorizontalStack>
      </Box>
    </Box>
  );
};

InsightSection.defaultProps = {
  className: '',
  onSubmit: noop,
};

InsightSection.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  onSubmit: PropTypes.func,
};

export default withStyles(styles)(InsightSection);
